"use client";

import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { SessionDetailModal } from "@/components/session-detail-modal";
import { SessionFilterBar } from "@/components/session-filter-bar";
import { SessionTable } from "@/components/session-table";
import { useOpenSessionParam } from "@/hooks/useOpenSessionParam";
import { useQueryState } from "@/hooks/useQueryState";
import {
  listSessions,
  listPlaybooks,
  listBatches,
  getSessionTags,
  getSessionNeighbor,
  listIntentDirections,
  type SessionRecord,
  type PlaybookSummary,
  type BatchRecord,
  type AuditStatus,
} from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ALL = "__all__";

const QUERY_DEFAULTS: Record<string, string> = {
  playbook: ALL,
  tags: "",
  intent_directions: "",
  batch: ALL,
  start: "",
  end: "",
  outcome: ALL,
  overall: "",
  goal: "",
  intent: "",
  turns: "",
  audit_status: "",
  amd_filter: "",
  phone: "",
  call_group_id: "",
  min_duration: "",
  page_size: "50",
  offset: "0",
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

const HISTORY_COLUMNS = [
  "time",
  "source",
  "phone",
  "playbook",
  "turns",
  "duration",
  "intent",
  "intentDirections",
  "tags",
  "audited",
  "status",
  "outcome",
  "callState",
  "overall",
  "goal",
  "evalBar",
  "amd",
] as const;

export default function HistoryPage() {
  return (
    <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">加载中...</div>}>
      <HistoryPageInner />
    </Suspense>
  );
}

function HistoryPageInner() {
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useQueryState(QUERY_DEFAULTS);
  const filterPlaybook = query.playbook;
  const filterTags = useMemo(
    () => (query.tags ? query.tags.split(",").filter(Boolean) : []),
    [query.tags],
  );
  const filterIntentDirs = useMemo(
    () => (query.intent_directions ? query.intent_directions.split(",").filter(Boolean) : []),
    [query.intent_directions],
  );
  const filterIntent = query.intent;
  const filterBatch = query.batch;
  const startDate = query.start;
  const endDate = query.end;
  const filterOutcome = query.outcome;
  const filterOverall = query.overall;
  const filterGoal = query.goal;
  const filterTurns = query.turns;
  const filterAuditStatus = query.audit_status as AuditStatus;
  const amdFilter = (query.amd_filter || undefined) as "only" | "exclude" | undefined;
  const limit = Number(query.page_size) || 50;
  const offset = Number(query.offset) || 0;

  const [playbooks, setPlaybooks] = useState<PlaybookSummary[]>([]);
  const [batches, setBatches] = useState<BatchRecord[]>([]);
  const [tagsByPlaybook, setTagsByPlaybook] = useState<Record<string, string[]>>({});
  const [dirsByPlaybook, setDirsByPlaybook] = useState<Record<string, string[]>>({});
  const availableTags = filterPlaybook === ALL ? [] : (tagsByPlaybook[filterPlaybook] ?? []);
  const availableIntentDirs = filterPlaybook === ALL ? [] : (dirsByPlaybook[filterPlaybook] ?? []);

  const { openId, pushOpen, replaceOpen, closeOpen } = useOpenSessionParam();

  useEffect(() => {
    Promise.all([listPlaybooks(), listBatches()]).then(([pb, b]) => {
      setPlaybooks(pb.playbooks);
      setBatches(b.batches);
    });
  }, []);

  useEffect(() => {
    if (filterPlaybook === ALL) return;
    let cancelled = false;
    getSessionTags(filterPlaybook).then((ts) => {
      if (!cancelled) setTagsByPlaybook((m) => ({ ...m, [filterPlaybook]: ts.tags }));
    });
    listIntentDirections(filterPlaybook).then((r) => {
      if (!cancelled) setDirsByPlaybook((m) => ({ ...m, [filterPlaybook]: r.directions }));
    });
    return () => {
      cancelled = true;
    };
  }, [filterPlaybook]);

  const pbParam = filterPlaybook === ALL ? "" : filterPlaybook;
  const batchParam = filterBatch === ALL ? "" : filterBatch;
  const startTs = startDate ? new Date(startDate).getTime() / 1000 : 0;
  const endTs = endDate ? new Date(endDate + "T23:59:59").getTime() / 1000 : 0;
  const outcomeParam = filterOutcome === ALL ? "" : filterOutcome;
  const minOverall = filterOverall ? Number(filterOverall) / 100 : 0;
  const minGoal = filterGoal ? Number(filterGoal) / 100 : 0;
  const minIntent = filterIntent ? Number(filterIntent) / 100 : 0;
  const minTurns = Number(filterTurns) || 0;
  const minDuration = Number(query.min_duration) || 0;

  useEffect(() => {
    let cancelled = false;
    // Only show spinner if the fetch takes > 100ms — avoids flicker on fast responses.
    const showLoadingTimer = setTimeout(() => {
      if (!cancelled) setLoading(true);
    }, 100);
    listSessions({
      limit,
      offset,
      playbook_id: pbParam,
      tags: filterTags,
      intent_directions: filterIntentDirs,
      batch_id: batchParam,
      start_date: startTs,
      end_date: endTs,
      outcome: outcomeParam,
      min_overall: minOverall,
      min_goal: minGoal,
      min_intent: minIntent,
      min_turns: minTurns,
      audit_status: filterAuditStatus,
      amd_filter: amdFilter,
      phone: query.phone,
      min_duration: minDuration,
      call_group_id: query.call_group_id,
    }).then((res) => {
      clearTimeout(showLoadingTimer);
      if (!cancelled) {
        setSessions(res.sessions);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      clearTimeout(showLoadingTimer);
    };
  }, [limit, offset, pbParam, filterTags, filterIntentDirs, batchParam, startTs, endTs, outcomeParam, minOverall, minGoal, minIntent, minTurns, filterAuditStatus, amdFilter, query.phone, query.call_group_id, minDuration]);

  const fetchNeighbor = useCallback(
    (direction: "prev" | "next") => {
      return getSessionNeighbor(openId!, direction, {
        playbook_id: pbParam,
        tags: filterTags,
        intent_directions: filterIntentDirs,
        batch_id: batchParam,
        start_date: startTs,
        end_date: endTs,
        outcome: outcomeParam,
        min_overall: minOverall,
        min_goal: minGoal,
        min_intent: minIntent,
        min_turns: minTurns,
        audit_status: filterAuditStatus,
        amd_filter: amdFilter,
        phone: query.phone,
        min_duration: minDuration,
        call_group_id: query.call_group_id,
      });
    },
    [openId, pbParam, filterTags, filterIntentDirs, batchParam, startTs, endTs, outcomeParam, minOverall, minGoal, minIntent, minTurns, filterAuditStatus, amdFilter, query.phone, query.call_group_id, minDuration],
  );

  return (
    <div className="flex h-full flex-col">
      <SessionFilterBar
        query={query}
        setQuery={setQuery}
        availableTags={availableTags}
        availableIntentDirections={availableIntentDirs}
        playbooks={playbooks}
        batches={batches}
        showPhone
        showCallGroup
      />

      <div className="flex-1 overflow-auto">
        <SessionTable
          sessions={sessions}
          columnKeys={HISTORY_COLUMNS}
          onOpenRow={(id) => (openId ? replaceOpen(id) : pushOpen(id))}
          loading={loading}
        />
      </div>

      <div className="flex items-center justify-between border-t px-4 py-2">
        <Button
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => setQuery({ offset: String(Math.max(0, offset - limit)) })}
        >
          上一页
        </Button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            显示 {offset + 1}&ndash;{offset + sessions.length}
          </span>
          <Select
            value={String(limit)}
            onValueChange={(v) => setQuery({ page_size: v, offset: "0" })}
          >
            <SelectTrigger className="h-8 w-20 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={sessions.length < limit}
          onClick={() => setQuery({ offset: String(offset + limit) })}
        >
          下一页
        </Button>
      </div>

      <SessionDetailModal
        open={!!openId}
        sessionId={openId}
        onFetchNeighbor={fetchNeighbor}
        onClose={closeOpen}
        onNavigate={replaceOpen}
      />
    </div>
  );
}
